/**
 * Recently viewed: reads handles from localStorage, renders cards via Section Rendering API
 * using the same `_product-card` / `static-product-card` slice as product recommendations.
 */
import { buildSectionRenderingURL, normalizeSectionId } from '@theme/section-renderer';

const STORAGE_KEY = 'dossier_recently_viewed_v1';
const MAX_STORED = 20;

/** @param {unknown[]} raw @returns {{ id: string, handle: string }[]} */
function normalizeStoredList(raw) {
  if (!Array.isArray(raw)) return [];
  /** @type {{ id: string, handle: string }[]} */
  const out = [];
  for (const e of raw) {
    if (typeof e === 'string' && e) {
      out.push({ id: '', handle: e });
      continue;
    }
    if (e && typeof e === 'object' && 'handle' in e && typeof e.handle === 'string') {
      const id = 'id' in e && e.id != null ? String(e.id) : '';
      out.push({ id, handle: e.handle });
    }
  }
  return out;
}

export class RecentlyViewed {
  /**
   * @param {string} id - Product id (for predictive search)
   * @param {string} handle - Product handle
   */
  static addProduct(id, handle) {
    if (!handle) return;
    try {
      let list = normalizeStoredList(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
      list = list.filter((e) => e.handle !== handle);
      list.push({ id: id != null ? String(id) : '', handle });
      if (list.length > MAX_STORED) list = list.slice(list.length - MAX_STORED);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (_) {}
  }

  /** @returns {string[]} Product ids (for predictive search `id:` queries). */
  static getProducts() {
    try {
      const list = normalizeStoredList(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
      return list.map((e) => e.id).filter(Boolean);
    } catch {
      return [];
    }
  }

  static clearProducts() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  }
}

/** @param {string} excludeHandle @param {number} limit */
function getHandles(excludeHandle, limit) {
  try {
    let list = normalizeStoredList(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
    list = list.filter((e) => e.handle && e.handle !== excludeHandle);
    return list
      .slice(-limit)
      .reverse()
      .map((e) => e.handle);
  } catch {
    return [];
  }
}

function resolveSliceSectionId() {
  const el = document.querySelector('[data-recently-viewed-card-slice]');
  if (!el) return '';
  const section = el.closest('.shopify-section');
  if (!section?.id) return '';
  return normalizeSectionId(section.id);
}

/**
 * @param {string} handle
 * @param {string} sliceSectionId
 * @returns {Promise<string | null>} outerHTML of `.resource-list__item` or null
 */
async function fetchRenderedCardItemHtml(handle, sliceSectionId) {
  if (!sliceSectionId || !handle) return null;
  const shopifyRoutes = /** @type {{ routes?: { root?: string } } | undefined} */ (window.Shopify);
  const root = shopifyRoutes?.routes?.root || '/';
  const productUrl = new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
  const url = buildSectionRenderingURL(sliceSectionId, productUrl);
  const res = await fetch(url, { headers: { Accept: 'text/html' } });
  if (!res.ok) return null;
  const text = await res.text();
  const doc = new DOMParser().parseFromString(text, 'text/html');
  const sectionEl = doc.getElementById(`shopify-section-${normalizeSectionId(sliceSectionId)}`);
  const item = sectionEl?.querySelector('.resource-list__item');
  return item ? item.outerHTML : null;
}

/** @param {number} cents */
function formatMoney(cents) {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return '';
  try {
    const Sh = /** @type {{ formatMoney?: (n: number, fmt: string) => string, currency?: { format?: string } }} */ (
      window.Shopify
    );
    if (Sh && typeof Sh.formatMoney === 'function') {
      const w = /** @type {Window & { money_format?: string, money_without_trailing_zeros_format?: string }} */ (window);
      const fmt = w.money_without_trailing_zeros_format || w.money_format || Sh.currency?.format;
      return Sh.formatMoney(cents, fmt || '${{amount}}');
    }
  } catch (_) {}
  const cur = /** @type {{ currency?: { active?: string } }} */ (window.Shopify);
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur?.currency?.active || 'USD' }).format(
    cents / 100
  );
}

/** @param {string} s */
function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * @param {Record<string, unknown>} p - Product JSON from /products/handle.js
 */
function cardHtmlFallback(p) {
  const rawImg = /** @type {unknown} */ (p.featured_image) || (Array.isArray(p.images) && p.images[0]);
  const src =
    typeof rawImg === 'string'
      ? rawImg.startsWith('//')
        ? `https:${rawImg}`
        : rawImg
      : rawImg?.src
        ? String(rawImg.src).startsWith('//')
          ? `https:${rawImg.src}`
          : rawImg.src
        : null;
  const variant =
    /** @type {{ url?: string, compare_at_price?: unknown, price?: unknown } | undefined} */ (
      p.selected_or_first_available_variant || (Array.isArray(p.variants) ? p.variants[0] : undefined)
    );
  const url = variant?.url || /** @type {string} */ (p.url) || '#';
  const title = escapeHtml(String(p.title || ''));
  const compare = variant?.compare_at_price;
  const price = variant?.price ?? p.price_min;
  const priceNum = typeof price === 'number' ? price : parseInt(String(price ?? '').replace(/\D/g, ''), 10) || 0;
  const priceStr = formatMoney(priceNum);
  const compareNum =
    typeof compare === 'number' ? compare : parseInt(String(compare ?? '').replace(/\D/g, ''), 10) || 0;
  const compareStr = compareNum > priceNum ? formatMoney(compareNum) : '';

  const imgTag = src
    ? `<img class="recently-viewed-card__img" src="${escapeHtml(src)}" alt="${title}" width="600" height="600" loading="lazy">`
    : `<div class="recently-viewed-card__img recently-viewed-card__img--placeholder" aria-hidden="true"></div>`;

  const priceBlock =
    compareStr && compareStr !== priceStr
      ? `<span class="recently-viewed-card__price"><span class="recently-viewed-card__compare">${compareStr}</span> ${priceStr}</span>`
      : `<span class="recently-viewed-card__price">${priceStr}</span>`;

  return `
<div class="resource-list__item">
  <div class="recently-viewed-card product-grid__card">
    <a href="${escapeHtml(url)}" class="recently-viewed-card__link">
      ${imgTag}
      <span class="recently-viewed-card__title">${title}</span>
      ${priceBlock}
    </a>
  </div>
</div>`;
}

/** @param {string} handle */
async function fetchProductJson(handle) {
  const shopify = /** @type {{ routes?: { root?: string } } | undefined} */ (window.Shopify);
  const root = shopify?.routes?.root || '/';
  const res = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return null;
  return res.json();
}

/**
 * @param {HTMLElement} root
 * @param {{ limit: number, excludeHandle?: string, sliceSectionId?: string }} opts
 */
export async function renderRecentlyViewedInto(root, opts) {
  const limit = opts.limit || 8;
  const exclude = opts.excludeHandle || '';
  const handles = getHandles(exclude, limit);
  const sliceId = opts.sliceSectionId || resolveSliceSectionId();

  root.innerHTML = '';
  root.dataset.hasRecommendations = 'false';

  if (!handles.length) {
    root.classList.add('recently-viewed-products--empty');
    return;
  }

  root.classList.remove('recently-viewed-products--empty');

  const fragments = await Promise.all(
    handles.map(async (h) => {
      try {
        if (sliceId) {
          const html = await fetchRenderedCardItemHtml(h, sliceId);
          if (html) return html;
        }
        const p = await fetchProductJson(h);
        return p ? cardHtmlFallback(p) : '';
      } catch {
        return '';
      }
    })
  );

  const html = fragments.filter(Boolean).join('');
  if (!html) {
    root.classList.add('recently-viewed-products--empty');
    return;
  }
  root.innerHTML = html;
  root.dataset.hasRecommendations = 'true';
}

/** @param {Element} sectionRoot */
function initDualTabs(sectionRoot) {
  const tabs = sectionRoot.querySelectorAll('.product-recs-dual__tab');
  const panels = sectionRoot.querySelectorAll('.product-recs-dual__panel');
  if (!tabs.length || !panels.length) return;

  tabs.forEach((node) => {
    const btn = /** @type {HTMLElement} */ (node);
    btn.addEventListener('click', () => {
      const tabId = btn.dataset.tab;
      tabs.forEach((tn) => {
        const t = /** @type {HTMLElement} */ (tn);
        const on = t.dataset.tab === tabId;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.classList.toggle('is-active', on);
      });
      panels.forEach((pn) => {
        const p = /** @type {HTMLElement} */ (pn);
        const on = p.dataset.panel === tabId;
        p.toggleAttribute('hidden', !on);
        p.classList.toggle('is-active', on);
      });
    });
  });
}

function initRoots() {
  const sliceId = resolveSliceSectionId();

  document.querySelectorAll('[data-recently-viewed-root]').forEach((node) => {
    const el = /** @type {HTMLElement} */ (node);
    if (el.dataset.recentlyViewedInit === '1') return;
    el.dataset.recentlyViewedInit = '1';
    const limit = parseInt(el.dataset.limit || '8', 10) || 8;
    const exclude = el.dataset.excludeHandle || '';
    renderRecentlyViewedInto(el, { limit, excludeHandle: exclude, sliceSectionId: sliceId });
  });

  document.querySelectorAll('.product-recs-dual').forEach((node) => {
    const dual = /** @type {HTMLElement} */ (node);
    if (dual.dataset.dualInit === '1') return;
    dual.dataset.dualInit = '1';
    initDualTabs(dual);
  });
}

document.addEventListener('DOMContentLoaded', initRoots);
document.addEventListener('shopify:section:load', initRoots);
